import { defineStore } from 'pinia'
import UnitService from '@/api/UnitService'
import ClientService from '@/api/ClientService'
import { useUserStore } from './user'

export const useUnitStore = defineStore('unit', {
    state: () => ({
        unitData: {
            id: null,
            name: '',
            client_id: null,
            country_id: '',
            city: '',
            street: '',
            postal_code: '',
            unit_type_id: '',
            year: '',
            oem_id: '',
            source_id: '',
            current_state: '',
            last_seen: '',
            last_produced_at: '',
            last_online_at: '',
            lifetime_utilization_fraction: 0,
            notes: '{}',
            begin: null,
            billing_unit_name: null,
            has_iot_connection: null,
        },

        originalUnitData: null,

        actualUsage: [],
        plannedUsage: [],

        unitTypeOptions: [],

        connectivity_settings: {
            connection_type: 'default_connection_type',
            configuration: '{}',
            connection_types: [],
        },

        grouping: 'day',

        loading: false,
        saving: false,
        saving_connectivity: false,
        loading_usage: false,
        loading_base_info: false,
        usageDataLoaded: false,
        downloading: false,
        loadingClientAddress: false,
    }),

    getters: {},

    actions: {
        _userStore() {
            return useUserStore()
        },

        notification(severity, detail) {
            const summary = severity === 'error' ? 'Error' : 'Success'
            this.toast.add({
                severity,
                summary,
                detail,
                life: this.getToastDuration(detail, severity),
            })
        },

        resetUnitData() {
            this.unitData = {
                id: null,
                name: '',
                client_id: null,
                country_id: '',
                city: '',
                street: '',
                postal_code: '',
                unit_type_id: '',
                year: '',
                oem_id: '',
                source_id: '',
                current_state: '',
                last_seen: '',
                last_produced_at: '',
                last_online_at: '',
                lifetime_utilization_fraction: 0,
                notes: '{}',
                begin: null,
                billing_unit_name: null,
                has_iot_connection: null,
            }

            this.connectivity_settings = {
                connection_type: 'default_connection_type',
                configuration: '{}',
                connection_types: [],
            }

            this.originalUnitData = null

            this.actualUsage = []
            this.plannedUsage = []

            this.unitTypeOptions = []

            this.grouping = 'day'

            this.loading = false
            this.saving = false
            this.saving_connectivity = false
            this.loading_usage = false
            this.loading_base_info = false
            this.usageDataLoaded = false
            this.downloading = false
        },

        async getUnitData(unit) {
            this.loading = true
            try {
                const { unitData, notes, connectivity_settings, unit_type_options } =
                    await UnitService.get(unit)
                Object.keys(unitData).forEach((key) => {
                    this.unitData[key] = unitData[key]
                })

                this.unitTypeOptions = unit_type_options

                if (notes) {
                    this.unitData.notes = notes
                }

                if (this._userStore().can('manage equipment connectivity')) {
                    this.connectivity_settings.connection_type =
                        connectivity_settings.connection_type
                    this.connectivity_settings.configuration = JSON.stringify(
                        connectivity_settings.configuration
                    )
                    this.connectivity_settings.connection_types =
                        connectivity_settings.connection_types.map((type) => {
                            return {
                                label: type,
                                value: type,
                            }
                        })
                }

                this.originalUnitData = JSON.parse(JSON.stringify(this.unitData))
            } catch (error) {
                console.error('Error fetching unit data (stores/unit/getUnitData):', error)
            } finally {
                this.loading = false
            }
        },

        revertUnitData() {
            if (!this.originalUnitData) return
            Object.keys(this.originalUnitData).forEach((key) => {
                this.unitData[key] = this.originalUnitData[key]
            })
        },

        async getUnitBaseInfo(unit) {
            this.loading_base_info = true
            try {
                const { data } = await UnitService.getBaseInfo(unit)
                this.unitData.id = unit
                this.unitData.name = data.name
                this.unitData.source_id = data.source_id
                this.unitData.billing_unit_name = data.billing_unit_name
            } catch (error) {
                console.error('Error fetching unit base info (stores/unit/getUnitBaseInfo):', error)
                this.notification('error', '§ui_message.load_failed')
            } finally {
                this.loading_base_info = false
            }
        },

        async update() {
            this.saving = true
            try {
                await UnitService.update({ unit: this.unitData.id }, { ...this.unitData })
                this.originalUnitData = JSON.parse(JSON.stringify(this.unitData))
                this.notification('success', '§ui_message.update_successful')
            } catch (error) {
                console.error('Error updating unit data (stores/unit/update):', error)
                this.notification('error', '§ui_message.update_failed')
            } finally {
                this.saving = false
            }
        },

        async copyClientAddress() {
            this.loading = true
            this.loadingClientAddress = true
            try {
                const data = await ClientService.get(this.unitData.client_id)
                return {
                    country_id: data.country_id,
                    city: data.city,
                    street: data.street,
                    postal_code: data.postal_code,
                }
            } catch (error) {
                console.error('Error updating unit data (stores/unit/copyClientAddress):', error)
                this.notification('error', '§ui_message.client_address_load_failed')
            } finally {
                this.loading = false
                this.loadingClientAddress = false
            }
        },

        async saveConnectivity(connection_type, configuration) {
            this.saving_connectivity = true
            try {
                await UnitService.saveConnectivity(
                    { unit: this.unitData.id },
                    { connection_type, configuration }
                )
                this.connectivity_settings.connection_type = connection_type
                this.connectivity_settings.configuration = configuration
                this.notification('success', '§ui_message.update_successful')
            } catch (error) {
                console.error(
                    'Error updating unit configuration data (stores/unit/saveConnectivity):',
                    error
                )
                this.notification('error', '§ui_message.update_failed')
            } finally {
                this.saving_connectivity = false
            }
        },

        async getUseData() {
            this.usageDataLoaded = false
            this.loading_usage = true
            try {
                const { data } = await UnitService.getUseData(
                    { id: this.unitData.id },
                    this.grouping
                )
                this.actualUsage = data.actual_usage || []
                this.plannedUsage = data.planned_usage || []
                this.unitData.begin = data.begin || null
                this.usageDataLoaded = true
            } catch (error) {
                console.error('Error fetching unit usage data (stores/unit/getUseData):', error)
                this.notification('error', '§ui_message.load_failed')
            } finally {
                this.loading_usage = false
            }
        },

        async downloadUnitUseExport() {
            this.downloading = true
            try {
                const filename = `unit_usage_${this.unitData.id}_${this.grouping}.xlsx`
                await UnitService.downloadUnitUseExport(filename, {
                    unit: this.unitData.id,
                    grouping: this.grouping,
                    export: 'excel',
                })
            } catch (error) {
                console.error(
                    'Error downloading unit usage export (stores/unit/downloadUnitUseExport):',
                    error
                )
                this.notification('error', '§ui_message.download_failed')
            } finally {
                this.downloading = false
            }
        },
    },
})
